

import CloudDoneIcon from '@mui/icons-material/CloudDone'; // 🟢 Synchronisiert
import CloudSyncIcon from '@mui/icons-material/CloudSync'; // 🟡 Bearbeitet
import { marked } from 'marked';
import { Menu, MenuItem } from "@mui/material";
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from "docx";
// import { saveAs } from "file-saver";
import ConfigureDialogContent from "./ConfigureDialogContent";
import ConfigureDialogReportContent from "./ConfigureDialogReportContent";
import ConfigureDialog from "./ConfigureDialog";
import ConfigureDialogReport from "./ConfigureDialogReport";

import ReactQuill, { Quill } from "react-quill";
import 'react-quill/dist/quill.snow.css';
import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { useSessionStore, labelMap } from "./SessionStore"; // Zustand-Store für die aktuelle Sitzung
import Tooltip from './components/Tooltip';
import { useDialog } from './components/Modal/DialogContext';
import { transcribeDictation } from "./utils/dictation";

import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MicOffIcon from '@mui/icons-material/MicOff';
import SessionDetails, { DEFAULT_NOTE_HEADER_FIELDS, getNoteHeaderEntries, type NoteHeaderFieldId } from './SessionDetails';

import html2md from 'html-to-md';
import { useSiteSettings } from './context/SiteSettingsContext';

const ClipboardBase = Quill.import("modules/clipboard");
const Delta = Quill.import("delta");
let isCustomClipboardRegistered = false;

class CustomClipboard extends ClipboardBase {
  onPaste(e: ClipboardEvent) {
    if (e.defaultPrevented || !this.quill.isEnabled()) return;
    e.preventDefault();
    const range = this.quill.getSelection(true);
    if (!range) return;
    const data = e.clipboardData;
    const html = data?.getData("text/html") || "";
    const text = data?.getData("text/plain") || "";
    const delta = html ? this.convert(html) : new Delta().insert(text);
    const composed = new Delta().retain(range.index).concat(delta).delete(range.length);
    this.quill.updateContents(composed, "user");
    this.quill.setSelection(range.index + delta.length(), "silent");
  }
}

if (!isCustomClipboardRegistered) {
  Quill.register("modules/clipboard", CustomClipboard, true);
  isCustomClipboardRegistered = true;
}

// Acceptable AppMode values
type AppMode = "session" | "case";

type DocumentationProps = {
  mode: AppMode;
};

const Documentation: React.FC<DocumentationProps> = ({ mode }) => {
  // Binde sessionStore ein
  const { settings: siteSettings, loading: isSiteSettingsLoading } = useSiteSettings();
  const logLevel = siteSettings?.frontend_log_level ?? 0;;
  const sessions = useSessionStore((s) => s.sessions);
  const dialog = useDialog();
  const { selectedSessionId } = useSessionStore();
  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(null);
  const { sessionData, fetchSessionDataById, selectedDocId } = useSessionStore();
  const [isEdited, setIsEdited] = useState(false);

  // Zustand-Store für die aktuelle Sitzung
  const [editorContent, setEditorContent] = useState('');
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});
  const [markdownDoku, setMarkdownDoku] = useState<string | "">(sessionData?.documentation?.documentation || "");
  const [htmlContent, setHtmlContent] = useState('');
  const quillRef = useRef<ReactQuill | null>(null);
  const isEditedRef = useRef(false);
  // Ref, um System-Updates von User-Edits zu unterscheiden

  const isSystemUpdate = useRef(false);

  // Ref, um die zuletzt geladene SessionId zu speichern
  const lastLoadedSessionId = useRef<number | null>(null);
  const lastLoadedDocId = useRef<number | null>(null);


  // Für temporäre Statusänderungen
  const currentSession = sessions.find((s) => s.id === selectedSessionId);
  const isCompleted = currentSession?.tempStatus === "completed";

  const [copyFeedbackVisible, setCopyFeedbackVisible] = useState(false);
  const [selectedLlm, setSelectedLlm] = useState<string>("meta-llama/Llama-3.3-70B-Instruct");
  const [loading, setLoading] = useState(false);
  const editorContentRef = useRef('');
  const htmlContentRef = useRef('');

  const [quillVisible, setQuillVisible] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);


  const [dialogOpen, setDialogOpen] = useState(false);
  const [promptTemplates, setPromptTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [isKjp, setIsKjp] = useState(false);
  const [noteHeaderFields, setNoteHeaderFields] = useState<NoteHeaderFieldId[]>(DEFAULT_NOTE_HEADER_FIELDS);
  const isKjpActive = isKjp;
  const [llmModels, setLlmModels] = useState<string[]>([
    "meta-llama/Llama-3.3-70B-Instruct",
    "mistralai/Mistral-7B-Instruct-v0.3",
    "meta-llama/Meta-Llama-3.1-8B-Instruct",
    "mistralai/Mixtral-8x7B-Instruct-v0.1",
  ]);

  // Dynamische, editierbare Felder aus der Dokumentation
  const [openDictation, setOpenDictation] = useState(false);
  const recordingRef = useRef(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [currentInputIndex, setCurrentInputIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [cursorPos, setCursorPos] = useState<{ x: number, y: number } | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const initialMountDone = useRef(false);
  const [quillMounted, setQuillMounted] = useState(true);
  // State für das Anzeigen/Ausblenden der SessionDetails-Komponente
  const [showSessionDetails, setShowSessionDetails] = useState(true);
  // Sichtbarkeit des Recording-Panels
  const [isRecordingVisible, setIsRecordingVisible] = useState(false);
  // Hilfsfunktion zur Dokumentations-Bereinigung für alle Export-/Kopierfunktionen


  const cleanDocumentation = (htmlContent: string, sessionData: any) => {
    // Erstellt Markdown aus HTML und wandelt zB *-Listenpunkte in - um
    // Vorberietung des Textes für den Export
    const markdown = html2md(htmlContent || '').replace(/^\*\s+/gm, '- '); // Wandelt *-Listenpunkte in - um
    let text = markdown
      .replace(/^(#{1,6} .+)(\n[^\n])/gm, '$1\n\n$2') // Leerzeile nach jeder Überschrift
      .replace(/^#{1,6}\s*(.*)$/gm, '$1')             // Entfernt alle Markdown-Überschriften
      .replace(/\*\*(.*?)\*\*/g, '$1')                // Entfernt **Fettschrift**
      // Genau eine Leerzeile zwischen beliebigem Inhalt und der nächsten Überschrift erzwingen
      .replace(/([^\n])\n(#{1,6}\s+)/g, '$1\n\n$2')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    const lengthInMinutes = sessionData?.length ? Math.round(Number(sessionData.length) / 60) : "Unbekannt";
    const rawDate = sessionData?.date ? new Date(sessionData.date) : null;
    const formattedDate = rawDate
      ? `${String(rawDate.getDate()).padStart(2, '0')}.${String(rawDate.getMonth() + 1).padStart(2, '0')}.${rawDate.getFullYear()}`
      : "Unbekannt";
    const header = `Name: ${sessionData?.name}\nDatum: ${formattedDate}\n\n`;  // Länge: ${lengthInMinutes} Minuten\n\n`;
    return header + text;
  };

  const cleanForClipboardHTML = (html: string) => {
    return (html || '')
      // Entferne Quill-spezifische/inline Attribute, damit Copy/Paste robuster ist
      .replace(/class="[^"]*"/g, '')
      .replace(/style="[^"]*"/g, '')
      // Entferne leere Absätze aus Quill
      .replace(/<p><br><\/p>/g, '')
      // WICHTIG: Mindestens eine Leerzeile zwischen Listen und der nächsten Überschrift
      // (Apple Notes rendert sonst oft "ohne Abstand")
      // Immer einen visuellen Abstand vor jeder Überschrift erzwingen (unabhängig vom vorherigen Inhalt)
      .replace(/<\/p>\s*(<h[1-6]\b[^>]*>)/gi, '</p><p><br></p>$1')
      .replace(/<\/(ul|ol)>\s*(<h[1-6]\b[^>]*>)/gi, '</$1><p><br></p>$2');
  };

  const buildHeaderPlain = (sessionData: any, showHeader: boolean) => {
    if (!showHeader) return '';
    const headerEntries = getNoteHeaderEntries(sessionData, noteHeaderFields, mode, { dateFormat: 'short' });
    if (headerEntries.length === 0) return '';
    return `${headerEntries.map((entry) => `${entry.label}: ${entry.value}`).join('\n')}\n\n`;
  };

  const buildHeaderHTML = (sessionData: any, showHeader: boolean) => {
    if (!showHeader) return '';
    const headerEntries = getNoteHeaderEntries(sessionData, noteHeaderFields, mode, { dateFormat: 'short' });
    if (headerEntries.length === 0) return '';
    return `<p>${headerEntries.map((entry) => `<strong>${entry.label}:</strong> ${entry.value}`).join('<br/>')}</p>`;
  };

  const markdownToDocxParagraphs = (markdown: string) => {
    const lines = (markdown || '').replace(/\r\n/g, '\n').split('\n');

    const parseInlineBold = (text: string) => {
      const runs: TextRun[] = [];
      const re = /\*\*(.+?)\*\*/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
          runs.push(new TextRun(text.slice(lastIndex, match.index)));
        }
        runs.push(new TextRun({ text: match[1], bold: true }));
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        runs.push(new TextRun(text.slice(lastIndex)));
      }
      return runs.length ? runs : [new TextRun(text)];
    };

    const paras: Paragraph[] = [];
    let prevWasList = false;

    for (const line of lines) {
      const trimmed = line.trim();

      // Leerzeile
      if (trimmed === '') {
        paras.push(new Paragraph(''));
        prevWasList = false;
        continue;
      }

      // Überschrift
      const h = /^(#{1,6})\s+(.*)$/.exec(line);
      if (h) {
        const level = h[1].length;
        const text = h[2].trim();

        if (prevWasList) {
          // Abstand zwischen Liste und Überschrift
          paras.push(new Paragraph(''));
        }

        const heading =
          level === 1 ? HeadingLevel.HEADING_1 :
          level === 2 ? HeadingLevel.HEADING_2 :
          level === 3 ? HeadingLevel.HEADING_3 :
          level === 4 ? HeadingLevel.HEADING_4 :
          level === 5 ? HeadingLevel.HEADING_5 :
                        HeadingLevel.HEADING_6;

        paras.push(
  new Paragraph({
    heading,
    children: [
      new TextRun({
        text,
        bold: true,
        color: "000000", // explizit schwarz
      }),
    ],
  })
);
        prevWasList = false;
        continue;
      }

      // Bullet-Liste
      const li = /^[-*+]\s+(.*)$/.exec(line);
      if (li) {
        paras.push(new Paragraph({
          children: parseInlineBold(li[1].trim()),
          bullet: { level: 0 },
        }));
        prevWasList = true;
        continue;
      }

      // Normaler Absatz (inkl. **bold**)
      paras.push(new Paragraph({ children: parseInlineBold(line) }));
      prevWasList = false;
    }

    return paras;
  };

  const cleanForPlaintext = (htmlContent: string, sessionData: any) => {
    const markdown = html2md(htmlContent || '').replace(/^\*\s+/gm, '- ');
    const text = markdown
      // Mindestens eine Leerzeile zwischen Listen-Block und nächster Überschrift
      .replace(/((?:\n[-*+]\s.*)+)\n(#{1,6}\s+)/g, '$1\n\n$2')
      // Überschriften im Plaintext erkennbar machen
      .replace(/^#{1,6}\s*(.*)$/gm, '$1:')
      // Markdown-Fett entfernen
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return buildHeaderPlain(sessionData, showSessionDetails) + text;
  };


  // Setze Editor-Inhalt bei Änderung der Dokumentation in sessionData
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (dialogOpen) {
        setDialogOpen(false); // 🔒 Dialog schließen statt zurücknavigieren
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [dialogOpen]);

  useEffect(() => {
    if (!sessionData?.documentation?.documentation) return;

    const currentDocId = sessionData.documentation.id ?? null;

    if (
      isEdited &&
      lastLoadedSessionId.current === sessionData?.id &&
      lastLoadedDocId.current === currentDocId
    ) {
      setIsEdited(false);
      isEditedRef.current = false;
      return;
    }

    const markdown = sessionData.documentation.documentation;
    const html = marked.parse(markdown) as string;
    lastLoadedSessionId.current = sessionData?.id;
    lastLoadedDocId.current = currentDocId;
    isSystemUpdate.current = true;

    // Editor kurz aus- und einblenden
    setQuillVisible(false);
    initialMountDone.current = false;

    setTimeout(() => {
      setEditorContent(html);

      // Dictation-Button beim ersten Mount korrekt disabled setzen
      setTimeout(() => {
        const toolbarButton = document.querySelector('.ql-toolbar button.ql-dictation') as HTMLButtonElement | null;
        if (toolbarButton) {
          toolbarButton.disabled = true;
        }
      }, 100);

      setTimeout(() => {
        const editor = quillRef.current?.getEditor();
        if (editor) {
          const history = editor.getModule('history');
          if (history && typeof history.clear === 'function') {
            history.clear();
          }
        }
      }, 100);
    }, 50);

    setIsEdited(false);
    if (!isSiteSettingsLoading && logLevel >= 3) console.log("New loaded:");
  }, [sessionData]);


  // Setze isEdited, wenn sich der Editor-Inhalt vom geladenen HTML unterscheidet
  useEffect(() => {
    if (isSystemUpdate.current) {
      isSystemUpdate.current = false;
      return;
    }
    if (editorContentRef.current === htmlContentRef.current) {
      setIsEdited(false);
      isEditedRef.current = false;
      return;
    }
    setIsEdited(true);
    isEditedRef.current = true;

  }, [editorContent]);

  useEffect(() => {
    if (!quillVisible) {
      setQuillMounted(false);
    }
  }, [quillVisible]);


  // Auto-Save nach 5 Sekunden Inaktivität
  useEffect(() => {

    if (sessionData?.documentation_locked) return;

    const timeout = setTimeout(async () => {
      if (!isEditedRef.current) return;
      if (!sessionData?.id) return;
      try {
        const markdown = html2md(editorContentRef.current);
        const existingData = sessionData?.documentation || {};
        const updatedData = {
          ...existingData,
          documentation: markdown,
        };

        const payload: Record<string, any> = {
          id: sessionData.id,
          documentation: updatedData,
        };
        if (selectedDocId) {
          payload.doc_id = selectedDocId;
        }
        await axios.put("/api/apps/update-session/", payload);
        htmlContentRef.current = editorContentRef.current; // HTML-Inhalt nach dem Speichern aktualisieren
        if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ Dokumentation automatisch gespeichert");
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Auto-Speichern der Dokumentation:", error);
      }
    }, 2000);

    return () => clearTimeout(timeout);
  }, [editorContent]);


  useEffect(() => {
    if (!quillMounted) {
      const timeout = setTimeout(() => {
        setQuillVisible(true);

        setQuillMounted(true);
      }, 50); // leicht verzögert

      return () => clearTimeout(timeout);
    }
  }, [quillMounted]);


  useEffect(() => {
    axios.get<{ is_kjp?: boolean; note_header_fields?: NoteHeaderFieldId[] }>("/api/apps/note-settings")
      .then((res) => {
        if (res.data.is_kjp !== undefined) {
          setIsKjp(!!res.data.is_kjp);
        }
        if (Array.isArray(res.data.note_header_fields) && res.data.note_header_fields.length > 0) {
          setNoteHeaderFields(res.data.note_header_fields);
        } else {
          setNoteHeaderFields(DEFAULT_NOTE_HEADER_FIELDS);
        }
      })
      .catch((err) => {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Laden der Note-Settings:", err);
      });
  }, []);

  // useEffect zum Laden der PromptTemplates

  useEffect(() => {
    const fetchPromptTemplates = async () => {
      try {
        const response = await axios.get("/api/apps/fetch-templates");
        setPromptTemplates(response.data);
      } catch (error) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Laden der PromptTemplates:", error);
      }
    };
    // Initiales Laden der PromptTemplates
    fetchPromptTemplates();
    const interval = setInterval(fetchPromptTemplates, 60000); // Automatische Aktualisierung alle 60 Sekunden
    return () => clearInterval(interval); // Beim Verlassen der Komponente den Timer löschen
  }, []);

  useEffect(() => {
    if (promptTemplates.length === 0) return;
    const eligible = promptTemplates.filter((tpl) => {
      if (isKjpActive) return !!tpl.for_kjp;
      return !tpl.for_kjp;
    });
    if (eligible.length === 0) return;
    const hasSelected = eligible.some((tpl) => tpl.id === selectedTemplate);
    if (selectedTemplate === null || !hasSelected) {
      setSelectedTemplate(eligible[0].id);
    }
  }, [promptTemplates, isKjpActive]);


  const [text, setText] = React.useState("");

  // add in the undo/redo icons

  var icons = Quill.import("ui/icons");
  icons["undo"] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="6 10 4 12 2 10 6 10"></polygon>
    <path class="ql-stroke" d="M8.09,13.91A4.6,4.6,0,0,0,9,14,5,5,0,1,0,4,9"></path>
  </svg>`;
  icons["redo"] = `<svg viewbox="0 0 18 18">
    <polygon class="ql-fill ql-stroke" points="12 10 14 12 16 10 12 10"></polygon>
    <path class="ql-stroke" d="M9.91,13.91A4.6,4.6,0,0,1,9,14a5,5,0,1,1,5-5"></path>
  </svg>`;
  icons["dictation"] = `<svg viewBox="0 0 22 22"><path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zm-5 9v-2h2v-2h-4v2h2v2h2z"/></svg>`;
  let quillEditor: any;


  const modules = useMemo(
    () => ({
      history: { delay: 200, maxStack: 500, userOnly: true },
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['clean'],
          ['undo', 'redo'],
          ['dictation'],
        ],
        handlers: {
          undo: () => {
            const editor = quillRef.current?.getEditor();
            editor?.getModule('history')?.undo?.();
          },
          redo: () => {
            const editor = quillRef.current?.getEditor();
            editor?.getModule('history')?.redo?.();
          },
          dictation: () => {
            // ---- Cursor check ganz oben einfügen ----
            const editor = quillRef.current?.getEditor();
            const range = editor?.getSelection();
            if (!range) {
              console.warn('⚠️ Kein Cursor im Editor. Diktat nicht gestartet.');
              return;
            }
            // -----------------------------------------
            if (recordingRef.current) {
              mediaRecorder?.stop();
              recordingRef.current = false;
              setIsRecordingVisible(false);
            } else {
              navigator.mediaDevices.getUserMedia({ audio: true })
                .then(stream => {
                  const recorder = new MediaRecorder(stream);

                  recorder.ondataavailable = (event) => {
                    if (event.data.size > 0) {
                      audioChunksRef.current.push(event.data);
                    }
                  };

                  recorder.onstop = async () => {
                    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

                    try {
                      const transcript = await transcribeDictation(audioBlob);
                      if (transcript) {
                        const editor = quillRef.current?.getEditor();
                        const range = editor?.getSelection();
                        if (editor && range) {
                          {/*const [leaf, offset] = editor.getLeaf(range.index);
        const node = leaf?.domNode?.parentNode as HTMLElement | null;
        if (node) {
          node.classList.remove('blurred'); // HIER: Blur wegnehmen direkt vor Einfügen
        }*/}

                          const prevChar = editor.getText(Math.max(0, range.index - 1), 1);
                          const nextChar = editor.getText(range.index, 1);
                          const needsSpaceBefore = prevChar && !/\s/.test(prevChar);
                          const needsSpaceAfter = nextChar && !/\s/.test(nextChar);
                          const finalText = `${needsSpaceBefore ? ' ' : ''}${transcript}${needsSpaceAfter ? ' ' : ''}`;

                          let currentIndex = 0;
                          const typeNextChar = () => {
                            if (currentIndex < finalText.length) {
                              const char = finalText[currentIndex];
                              editor.insertText(
                                range.index + currentIndex,
                                char,

                                "user"
                              );
                              currentIndex++;
                              setTimeout(typeNextChar, 13); // Geschwindigkeit anpassen (40ms pro Zeichen)
                            } else {
                              setIsRecordingVisible(false);
                              editor.setSelection(range.index, finalText.length);
                            }
                          };
                          typeNextChar();
                        }
                      }

                    } catch (error) {
                      console.error('❌ Fehler beim Transkribieren:', error);
                    }
                  };

                  audioChunksRef.current = [];
                  recorder.start();
                  setMediaRecorder(recorder);
                  recordingRef.current = true;
                  setIsRecordingVisible(true);

                  {/* const editorInstance = quillRef.current?.getEditor();
const rangeInstance = editorInstance?.getSelection();
if (editorInstance && rangeInstance) {
  const [leaf, offset] = editorInstance.getLeaf(rangeInstance.index);
  const node = leaf?.domNode?.parentNode as HTMLElement | null;
  if (node) {
    node.classList.add('blurred');
  }
}*/}
                })
                .catch(error => {
                  console.error('❌ Fehler beim Zugriff auf das Mikrofon:', error);
                });
            }
          },
        },
      },
      clipboard: {
        // toggle to add extra line breaks when pasting HTML:
        matchVisual: false,
      },
    }),
    [siteSettings]
  );



  const handleTextFieldClick = (index: number, event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
    const target = event.target as HTMLTextAreaElement | HTMLInputElement;
    if (!target.selectionStart) return; // Sicherstellen, dass es sich um ein Textfeld handelt

    const rect = target.getBoundingClientRect();
    const cursorX = rect.left + window.scrollX + target.selectionStart * 8; // Anpassung für Schriftgröße
    const cursorY = rect.top + window.scrollY - 30; // Button über dem Cursor positionieren

    setCurrentInputIndex(index);
    setCursorPos({ x: cursorX, y: cursorY });
  };

  const startRecording = () => {
    audioChunksRef.current = []; // 🔴 Vor jeder neuen Aufnahme alte Daten löschen

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(stream => {
        const recorder = new MediaRecorder(stream);

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            audioChunksRef.current.push(event.data); // 🔄 Direkt ins Ref speichern
          }
        };

        recorder.onstop = () => {
          if (!isSiteSettingsLoading && logLevel >= 3) console.log("📌 Aufnahme gestoppt, `audioChunksRef`:", audioChunksRef.current);
        };

        recorder.start();
        setMediaRecorder(recorder);
        //setRecording(true);
      })
      .catch(error => console.error("❌ Fehler beim Zugriff auf das Mikrofon:", error));
  };

  const saveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;

    try {
      if (!sessionData || !sessionData.id) {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler: sessionData oder sessionData.id ist null");
        return;
      }
      await axios.put("/api/services/update-documentation-fields/", {
        session_id: sessionData.id,
        updated_fields: pendingChanges
      });

      if (!isSiteSettingsLoading && logLevel >= 3) console.log("✅ Änderungen gespeichert:", pendingChanges);
      setPendingChanges({}); // Zurücksetzen nach dem Speichern
    } catch (error) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Speichern der Änderungen:", error);
    }
  };

  // Funktion zum Senden der Dokumentationsanforderung
  const handleGenerateDocumentation = async () => {
    if (!selectedTemplate) {
      alert("Bitte ein Prompt-Template auswählen!");
      return;
    }
    if (!sessionData || !sessionData.id) {
      alert("Keine Sitzung ausgewählt!");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post("/api/services/generate-documentation/", {
        user_sitzung_id: sessionData.id, // Sitzungs-ID aus SessionStore
        prompt_template_id: selectedTemplate,
        llm_model: selectedLlm
      });

    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        dialog({
          type: "alert",
          title: "Hinweis",
          message: error.response?.data?.error || "Maximal 4 Dokumentationen erlaubt. Bitte zuerst eine löschen.",
        });
        return;
      }
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("Fehler beim Generieren der Dokumentation:", error);
      alert("Fehler beim Generieren der Dokumentation.");
    } finally {
      setLoading(false);
    }
  };

  const updateDocumentationText = (newValue: string) => {
    setEditorContent(newValue);
  };

  const handleOpenDictation = () => {
    setOpenDictation(true);
    startRecording(); // 🔥 Aufnahme direkt starten!
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDownloadText = () => {
    const text = cleanForPlaintext(editorContentRef.current, sessionData);
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Luma_Doku_${sessionData?.date}_${sessionData?.name}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    handleClose();
  };

  const handleDownloadWord = async () => {
    const headerPlain = buildHeaderPlain(sessionData, showSessionDetails).trimEnd();
    const markdownBody = html2md(editorContentRef.current || '').replace(/^\*\s+/gm, '- ');

    // Header als erste Absätze, danach strukturierter Body
    const headerParas = headerPlain.split('\n').map(line => new Paragraph(line));
    const bodyParas = markdownToDocxParagraphs(markdownBody);

    const doc = new Document({
      sections: [
        {
          children: [...headerParas, new Paragraph(''), ...bodyParas],
        },
      ],
    });


    const blob = await Packer.toBlob(doc);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Luma_Doku_${sessionData?.date}_${sessionData?.name}.docx`;
    a.click();
    URL.revokeObjectURL(url);
    handleClose();
  };

  const buildPrintableHtml = () => {
    const bodyHtml = cleanForClipboardHTML(editorContentRef.current);
    const headerHtml = buildHeaderHTML(sessionData, showSessionDetails);

    return `
      <html>
        <head>
          <title>Luma_Doku_${sessionData?.date}_${sessionData?.name}</title>
          <meta charset="utf-8" />
          <style>
            @page { margin: 24mm; }
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; line-height: 1.4; font-size: 70%; }
            h1 { font-size: 1.04rem; margin: 1.4em 0 0.5em; }
            h2 { font-size: 0.92rem; margin: 1.2em 0 0.45em; }
            h3 { font-size: 1.05rem; margin: 1.1em 0 0.4em; }
            h4, h5, h6 { font-size: 1rem; margin: 1em 0 0.35em; }
            ul, ol { margin: 0.3em 0 0.0em; padding-left: 2.0em; }
            p { margin: 0.4em 0; }
          </style>
        </head>
        <body>
          ${headerHtml}
          ${bodyHtml}
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(buildPrintableHtml());
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
    handleClose();
  };

  const handleDownloadPDF = async () => {
    if (!sessionData?.id) {
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler: sessionData oder sessionData.id ist null");
      handleClose();
      return;
    }

    try {
      const filename = `Luma_Doku_${sessionData?.date}_${sessionData?.name}.pdf`;
      const response = await axios.post(
        `/api/services/documentation-pdf/${sessionData.id}`,
        { html: buildPrintableHtml(), filename },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      let message = "Fehler beim PDF-Download.";
      if (axios.isAxiosError(err) && err.response?.data) {
        if (err.response.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const data = JSON.parse(text);
            if (data?.error) message = data.error;
          } catch (parseErr) {
            if (!isSiteSettingsLoading && logLevel >= 2) console.error("❌ Fehler beim Parsen der Fehlermeldung:", parseErr);
          }
        } else if (err.response.data?.error) {
          message = err.response.data.error;
        }
      }
      alert(message);
      if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim PDF-Download:", err);
    }
    handleClose();
  };

  // Kopieren als Markdown-Text mit Bereinigung
  const handleCopyToClipboard = () => {
    const plainText = cleanForPlaintext(editorContentRef.current, sessionData);
    const html = cleanForClipboardHTML(editorContentRef.current);

    navigator.clipboard.write([
      new ClipboardItem({
        'text/plain': new Blob([plainText], { type: 'text/plain' }),
        'text/html': new Blob([html], { type: 'text/html' }),
      }),
    ])
      .then(() => {
        setCopyFeedbackVisible(true);
        setTimeout(() => setCopyFeedbackVisible(false), 2000);
      })
      .catch(err => {
        if (!isSiteSettingsLoading && logLevel >= 1) console.error("❌ Fehler beim Kopieren:", err);
      });
  };

  const handleOpenDialog = () => {
    const completedDocumentationCount = documentationList.filter(
      (docItem) => docItem.status === "completed"
    ).length;

    if (documentationList.length > 0 && completedDocumentationCount >= 4) {
      dialog({
        type: "alert",
        title: "Hinweis",
        message: "Maximal 4 Dokumentationen erlaubt. Bitte zuerst eine löschen.",
      });
      return;
    }

    setDialogOpen(true);
    window.history.pushState({ dialog: true }, '', '');
  };
  const documentationList = sessionData?.documentation_list ?? [];
  const orderedDocumentationList = [...documentationList].sort((a, b) => {
    const aTime = new Date(a.date).getTime();
    const bTime = new Date(b.date).getTime();
    return bTime - aTime;
  });
  const handleSelectDocumentation = async (docId: number) => {
    if (!sessionData?.id) return;
    if (selectedDocId === docId) return;
    await fetchSessionDataById(sessionData.id, docId);
  };
  const handleDeleteDocumentation = async (docId: number) => {
    if (!sessionData?.id) return;
    dialog({
      type: "confirm",
      title: "Dokumentation löschen",
      message: "Dokumentation wirklich löschen?",
      confirmButton: "Löschen",
      cancelButton: "Abbrechen",
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`/api/apps/docs/${docId}/`);
          const deletedIndex = orderedDocumentationList.findIndex((docItem) => docItem.id === docId);
          const remaining = orderedDocumentationList.filter((docItem) => docItem.id !== docId);
          let nextDocId = selectedDocId;
          if (docId === selectedDocId) {
            const left = deletedIndex > 0 ? orderedDocumentationList[deletedIndex - 1] : null;
            const right = deletedIndex >= 0 && deletedIndex < orderedDocumentationList.length - 1
              ? orderedDocumentationList[deletedIndex + 1]
              : null;
            nextDocId = left?.id ?? right?.id ?? null;
          }
          if (nextDocId && !remaining.some((docItem) => docItem.id === nextDocId)) {
            nextDocId = remaining[0]?.id ?? null;
          }
          if (nextDocId) {
            await fetchSessionDataById(sessionData.id, nextDocId);
          } else {
            await fetchSessionDataById(sessionData.id);
          }
        } catch (error) {
          if (!isSiteSettingsLoading && logLevel >= 1) {
            console.error("❌ Fehler beim Löschen der Dokumentation:", error);
          }
          dialog({
            type: "alert",
            title: "Fehler",
            message: "Fehler beim Löschen der Dokumentation.",
          });
        }
      },
    });
  };
  const formatDocLabel = (dateValue: string) => {
    const parsed = new Date(dateValue);
      if (Number.isNaN(parsed.getTime())) return dateValue;
    return parsed.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const docTypeLabelMap: Record<string, string> = {
    summary: "Zusammenfassung",
    progress_note: "Verlaufsdokumentation",
    therapy_note: "Therapienotiz",
    biography: "Biografie",
    intake: "Erstgespräch",
    supervision: "Supervision",
    other: "Andere",
  };
  const buildDocTabLabels = (docs: typeof documentationList) => {
    const labelsById: Record<number, string> = {};
    const counts: Record<string, number> = {};
    [...docs]
      .sort((a, b) => {
        const aTime = new Date(a.date).getTime();
        const bTime = new Date(b.date).getTime();
        return aTime - bTime || a.id - b.id;
      })
      .forEach((docItem) => {
        const docTypeKey = docItem.display_label || docItem.documentation_type || "other";
        const baseLabel = docItem.display_label || docTypeLabelMap[docTypeKey] || docTypeKey;
        const labelIndex = docItem.documentation_label_index;
        counts[docTypeKey] = Math.max(counts[docTypeKey] || 0, labelIndex || 0) + (labelIndex ? 0 : 1);
        const suffixIndex = labelIndex || counts[docTypeKey];
        const suffix = suffixIndex > 1 ? ` ${suffixIndex}` : "";
        labelsById[docItem.id] = `${baseLabel}${suffix}`;
      });

    return docs.map((docItem) => {
      return {
        ...docItem,
        tabLabel: labelsById[docItem.id],
      };
    });
  };
  const documentationTabs = buildDocTabLabels(orderedDocumentationList);
  const isModeCase = mode === "case";
  const isEntityCase = sessionData?.entity_type === "case";
  const transcript = sessionData?.data?.Transcript?.Text;
const ready = !!(transcript && transcript.trim());

  if (!ready || selectedSessionId === null) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '60vh',
        color: '#aaa',
        fontSize: '1.2rem',
        fontStyle: 'italic'
      }}>
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
          <svg className="animate-spin h-8 w-8 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8z" />
          </svg>
          <p className="tw-text-center tw-text-gray-500 tw-mt-2">
            {isModeCase
              ? "Die Dokumente werden analysiert. Anschließend können Sie hier die Dokumentation erstellen."
              : "Die Sitzung wird analysiert.  Anschließend können Sie hier die Dokumentation erstellen."}
          </p>
        </div>
      </div>
    );
  }

  if (!sessionData || sessionData.id !== selectedSessionId) {
    return null; // oder ein Lade-Spinner
  }
  const doc = sessionData?.documentation?.documentation;
  const documentationStatus = sessionData?.documentation?.status;
  const hasDocumentationContent = !!doc && doc.trim() !== '';
  const isFailedDocumentation = documentationStatus === "failed";
  const isFailedDocumentationRetryPending = isFailedDocumentation && !isCompleted;
  const canUseDocumentationActions = isCompleted && !isFailedDocumentation && hasDocumentationContent;
  if (!hasDocumentationContent && !isFailedDocumentation) {
    return (
      <>
        <div className="lg:w-[95%] max-h-[75vh] overflow-auto px-2 py-6 pt-6 pb-10 bg-white rounded-lg shadow-md">
          <div className="tw-grow tw-flex tw-flex-col">
            <div className="upload-form">
              <div className="upload-form__container">
                <div
                  className="upload-form__container__main__header tw-w-full sm:!w-[60vw]"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    minHeight: '4.5rem', // oder eine andere feste Höhe
                  }}
                >{isModeCase ? (<>
                  <h3 className="upload-form__container__main__h3">Neuen Bericht generieren</h3>

                  <p className="upload-form__container__main__p">
                    Vervollständigen Sie die Detail, um den Bericht zu erstellen 
                  </p></>
                ) : (
                  <>
                    <h3 className="upload-form__container__main__h3">Dokumentation generieren</h3>

                    <p className="upload-form__container__main__p">
                      Vervollständigen Sie die Sitzungdetails um die Dokumention zu erstellen
                    </p>
                  </>
                )}
                </div>
                {isModeCase ? (
                  <ConfigureDialogReportContent open={dialogOpen} setOpen={setDialogOpen} />
                ) : (
                  <ConfigureDialogContent open={dialogOpen} setOpen={setDialogOpen} />
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>
        {`
  .ql-editor ul:hover,
  .ql-editor li:hover {
    background: transparent !important;
    outline: none !important;
    box-shadow: none !important;
  }

  .ql-editor ul,
  .ql-editor ol,
  .ql-editor li {
    background: transparent !important;
    margin-top: 0.em;
    
  }

  .ql-editor h1,
  .ql-editor h2,
  .ql-editor h3,
  .ql-editor h4,
  .ql-editor h5,
  .ql-editor h6 {
    margin-top: 2.5em;
    margin-bottom: 0.3em;
  }
  @keyframes pulse {
    0% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.7; }
    100% { transform: scale(1); opacity: 1; }
  }
  .pulsing-mic {
    display: inline-block;
    animation: pulse 1s infinite ease-in-out;
  }
`}
      </style>


      <div className="_PageContent_kyudy_22">
        {documentationTabs.length > 1 && (
          <div
            className={`tw-flex tw-flex-wrap tw-gap-2 tw-mb-4 tw-px-2 ${
              !isCompleted ? "tw-pointer-events-none tw-opacity-60" : ""
            }`}
            aria-disabled={!isCompleted}
          >
            {documentationTabs.map((docItem) => {
              const isActive = selectedDocId === docItem.id;
              return (
                <div
                  key={docItem.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (!isCompleted) return;
                    handleSelectDocumentation(docItem.id);
                  }}
                  onKeyDown={(event) => {
                    if (!isCompleted) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectDocumentation(docItem.id);
                    }
                  }}
                  className={`flex items-center rounded-xl tw-border tw-px-4 tw-py-2 tw-text-sm tw-cursor-pointer session__card session__grab ${
                    isActive
                      ? "bg-[#faeed5] text-[#1f2937] border-[#1f2937] session__card--active"
                      : "bg-white text-[#1f2937] border-[#d1d5db]"
                  }`}
                >
                  <span className="tw-font-medium">
                    {docItem.tabLabel}
                  </span>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isCompleted) return;
                      handleDeleteDocumentation(docItem.id);
                    }}
                    className={`tw-ml-2 tw-text-base tw-text-[#1f2937]`}
                    aria-label="Dokumentation löschen"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        )}
        <div id="note-documentation" className="sign_notes">
          <div className="sign_notes__editor">
            <div className="sign_notes__editor__content">
              <div id="summary-header-row" className="summary-header-row tw-flex tw-items-center tw-justify-between tw-overscroll-auto tw-w-full">
                <div className="tw-flex tw-items-center tw-gap-3">
                  <h3 className="summary-section-title tw-mb-0">
                    <span className="">
                      {(hasDocumentationContent || isFailedDocumentation)
                        ? (isEntityCase
                            ? "Bericht"
                            : docTypeLabelMap[sessionData?.documentation?.documentation_type || "progress_note"])
                        : null}
                    </span>
                  </h3>
                </div>
                <div id="note-documentation-buttons" className="summary-buttons-section">

                  <div className="note-options tw-flex tw-items-center" id="note-options">
                    <Tooltip text="Ein-/ Ausblenden">
                      <div className="hidden md:tw-block" onClick={() => setShowSessionDetails(prev => !prev)}>
                        <div className="NA__container ">
                          <span className="toggle__NA "> Details</span>
                          <span
                            className={`toggle-round-button ${showSessionDetails ? 'active' : ''}${isModeCase ? ' !bg-[#E7A555]' : ''}`}
                          >
                            <div className={`toggle-round-button-inner ${showSessionDetails ? 'ball-active' : ''}`}></div>
                          </span>
                        </div>
                      </div>
                    </Tooltip>
                    <div className="summary-button pr-1 tw-cursor-default">
                      {sessionData?.documentation_locked ? (
                        <Tooltip text="Speichern nicht mehr möglich.">
                          <CloudDoneIcon sx={{ color: "#ccc", fontSize: 29 }} />
                        </Tooltip>
                      ) : isEdited ? (
                        <button data-state="closed" className=" summary-button summary-icon-records-button">
                          <Tooltip text="Speichern">
                            <CloudSyncIcon sx={{ color: "#eab64e", fontSize: 29 }} />
                          </Tooltip></button>
                      ) : (
                        <Tooltip text="Gespeichert">
                          <CloudDoneIcon sx={{ color: "#40937a", fontSize: 29 }} />
                        </Tooltip>
                      )}

                    </div>
                    <div>


                      <>
                        <Tooltip text="Herunterladen">
                          <button
                            type="button"
                            className="summary-button summary-icon-records-button"
                            disabled={!canUseDocumentationActions}
                            onClick={handleDownloadClick}
                          >
                            {/*!tw-hidden sm:!tw-flex <- für die button klasse zum verstecken */}
                            <img src="/download.png" alt="download icon" className="tw-w-[40px]" />
                          </button>
                        </Tooltip>

                        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                          <MenuItem onClick={handleDownloadText}>Reiner Text (.txt)</MenuItem>
                          <MenuItem onClick={handleDownloadWord}>Word (.docx)</MenuItem>
                          <MenuItem onClick={handleDownloadPDF}>PDF (.pdf)</MenuItem>
                          <MenuItem onClick={handlePrint}>Drucken</MenuItem>
                        </Menu>
                      </>
                    </div>
                    <div>
                      <button data-state="closed" disabled={!canUseDocumentationActions}></button>
                      {!copyFeedbackVisible && (<Tooltip text="Kopieren">
                        <button
                          type="button"
                          className="summary-button summary-icon-records-button"
                          disabled={!canUseDocumentationActions}
                          onClick={handleCopyToClipboard}
                        >
                          <img src="/copy.png" alt="copy icon" className="tw-w-[40px]" />
                        </button>
                      </Tooltip>)}

                      {copyFeedbackVisible && (
                        <span style={{ fontSize: '1.15rem', color: 'green', marginLeft: '0.2rem', marginRight: '0.5rem' }}>
                          ✔️
                        </span>
                      )}
                    </div>

                    {/* <div className="custom-dropdown">
                      <div className="dropdown-trigger-icon">
                        <img src="iscons/dropdown.png" alt=":" className="!w-z-1" />
                      </div>
                    </div>*/}
                  </div>
                  <div id="record-tooltip">
                      <button
                        type="button"
                        onClick={sessionData?.transcript_deleted ? undefined : () => handleOpenDialog()}
                        className={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _smButton_19rto_188 !tw-font-normal smButton tw-ml-4${isModeCase ? " !bg-[#E7A555]" : ""} `} 
                        disabled={!isCompleted || sessionData?.transcript_deleted}
                      >
                      <div className="_btnFlex_19rto_32">
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {!isCompleted ? (
                            <svg className="animate-spin h-5 w-5 " xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8z" />
                            </svg>
                          ) : sessionData?.transcript_deleted ? (
                            <>Gespeichert</>
                          ) : (
                            <>
                              <span className="hidden lg:inline">Neu erstellen</span>
                              <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 6V3L8 7l4 4V8c2.76 0 5 2.24 5 5a5.002 5.002 0 0 1-9.9 1H5.07a7.003 7.003 0 0 0 13.93-1c0-3.87-3.13-7-7-7z" />
                              </svg>
                            </>
                          )}
                        </span>
                      </div>
                    </button>
                  </div>
                  {/*<div id="record-tooltip">
                    <button type="button" className="_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _smButton_19rto_188 !tw-font-normal smButton">
                      <div className="_btnFlex_19rto_32">Save to records</div>
                    </button>
                  </div>*/}
                </div>
              </div>

              <div className="summary-section-column">
                <div className="event-note">
                  <div className="tw w-100">
                    {showSessionDetails && !isFailedDocumentation && <SessionDetails mode={mode} noteHeaderFields={noteHeaderFields} />}
                    {isFailedDocumentation ? (
                      <div
                        className="mt-6 flex w-full justify-center px-2 pb-10 sm:px-6"
                        style={{
                          maxHeight: "calc(100vh - 300px)",
                          overflowY: "auto",
                          overflowX: "hidden",
                          WebkitOverflowScrolling: "touch",
                          alignItems: "flex-start",
                          boxSizing: "border-box",
                        }}
                      >
                        <div className="relative w-full overflow-hidden rounded-2xl border border-[#d9e6ea] bg-white p-6 text-center shadow-[0_18px_45px_rgba(15,23,42,0.08)]">
                          {isFailedDocumentationRetryPending && (
                            <div
                              className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3"
                              style={{
                                background: "rgba(255, 255, 255, 0.68)",
                                backdropFilter: "blur(3px)",
                                WebkitBackdropFilter: "blur(3px)",
                              }}
                            >
                              <svg className="animate-spin h-7 w-7 text-[#6b7280]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8z" />
                              </svg>
                              <p className="m-0 text-sm font-medium text-[#374151]">
                                Neue Dokumentation wird erstellt
                              </p>
                            </div>
                          )}
                          <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${isModeCase ? "bg-[#fff1df]" : "bg-[#e8f4f7]"}`}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className={`h-7 w-7 ${isModeCase ? "text-[#E7A555]" : "text-[#2f7f96]"}`}
                              aria-hidden="true"
                            >
                              <path d="M12 9v4" />
                              <path d="M12 17h.01" />
                              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                            </svg>
                          </div>

                          <h3 className="mt-4 text-xl font-semibold text-[#17202a]">
                            Die Dokumentation konnte nicht erstellt werden
                          </h3>

                          <p className="mx-auto mt-3 max-w-4xl text-sm leading-6 text-[#4b5563]">
                            Beim letzten Versuch ist ein technischer Fehler aufgetreten. Die Dokumentation wurde nicht erstellt.
                          </p>

                          <div className="mx-auto mt-4 max-w-4xl rounded-2xl border border-[#bae6fd] bg-[#e0f2fe] p-4 text-left">
                            <div className="flex items-center gap-2">
                              <span className={`flex h-7 w-7 items-center justify-center rounded-full bg-white text-sm font-bold ${isModeCase ? "text-[#E7A555]" : "text-[#2f7f96]"}`}>
                                i
                              </span>
                              <p className="m-0 text-sm font-semibold text-[#1f2937]">
                                So geht es weiter
                              </p>
                            </div>
                            <p className="mt-2 text-sm leading-5 text-[#4b5563]">
                              Sie können die Erstellung erneut starten. Im nächsten Schritt öffnet sich das gewohnte Konfigurationsfenster.
                            </p>
                            <p className="mt-2 text-sm leading-5 text-[#4b5563]">
                              Sollte der Fehler wiederholt auftreten, prüfen wir die Ursache und beheben das Problem so schnell wie möglich.
                            </p>
                          </div>

                          <div className="mt-6 flex justify-center">
                            <button
                              type="button"
                              onClick={handleOpenDialog}
                              disabled={isFailedDocumentationRetryPending}
                              className={`_button_19rto_40 _primary_19rto_127 _filled_19rto_80 _smButton_19rto_188 !tw-font-normal smButton${isModeCase ? " !bg-[#E7A555]" : ""}`}
                            >
                              <div className="_btnFlex_19rto_32">Konfiguration öffnen und neu starten</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div dir="ltr" data-orientation="horizontal" style={{ display: "grid", gridTemplateRows: "auto 1fr", height: "100%", marginBottom: "10rem" }}>
                        <div className="tw-text-gray-500 tw-flex-1">
                          <div style={{ display: "grid", gridTemplateRows: "1fr auto", height: "100%" }}>
                            <div className="tw-flex tw-items-center tw-justify-between tw-overflow-hidden">
                              <div className="tw-flex tw-items-center tw-gap-3">
                                {/*<h3 className="summary-section-title tw-mb-0 tw-mt-5">
                                <span className="">Dokumentation</span>
                              </h3>*/}
                              </div>
                            </div>
                            <div id="onboarding-step-three" className="" data-state="active" data-orientation="horizontal" role="tabpanel" aria-labelledby="radix-320-trigger-clinical" >
                              <div className="tw-overflow-hidden">
                                <div className="  -container tw-relative">
                                  <div className="quill session-summary-textarea">
                                    {!quillVisible ? (
                                      <div style={{
                                        textAlign: "center",
                                        padding: "2rem",
                                        color: "#888",
                                        fontStyle: "italic"
                                      }}>
                                        <div className="tw-flex tw-items-center tw-gap-2 tw-justify-center">
                                          <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3.5-3.5L12 0v4a8 8 0 00-8 8z" />
                                          </svg>
                                          <span></span>
                                        </div>
                                      </div>
                                    ) : (
                                      isCompleted && quillMounted && (
                                        <div id="note-documentation-quill">
                                          <ReactQuill
                                            ref={quillRef}
                                            theme="snow"
                                            value={editorContent}
                                            modules={modules}
                                            onChange={(value) => {
                                              if (isSystemUpdate.current) {
                                                htmlContentRef.current = value;
                                                isSystemUpdate.current = false;
                                              }
                                              if (!initialMountDone.current) {
                                                htmlContentRef.current = value;
                                              }
                                              initialMountDone.current = true;
                                              setEditorContent(value);
                                              editorContentRef.current = value;
                                            }}
                                            placeholder="Schreiben Sie hier Ihre Notizen..."
                                            onKeyDown={(e) => {
                                              if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                                                e.stopPropagation(); // Verhindert, dass andere Komponenten diese Tasten abfangen
                                              }
                                            }}
                                            onChangeSelection={(range) => {
                                              const toolbarButton = document.querySelector('.ql-toolbar button.ql-dictation') as HTMLButtonElement | null;
                                              if (toolbarButton) {
                                                toolbarButton.disabled = !range;
                                              }
                                            }}
                                          /></div>
                                      )
                                    )}

                                  </div>
                                </div>
                                {/*  <div className="rating-section">
                          <button type="button" className="rating-icon">
                            <img src="documentation_files/ThumbsUpPrimary-bc366b81.svg" alt="Good" />
                          </button>
                          <span className="rating-text">Rate</span>
                          <button type="button" className="rating-icon">
                            <img src="documentation_files/ThumbsDownPrimary-fbbc4125.svg" alt="bad" />
                          </button>
                        </div>*/}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
      {isModeCase ? (
        <ConfigureDialogReport open={dialogOpen} setOpen={setDialogOpen} />
      ) : (
        <ConfigureDialog open={dialogOpen} setOpen={setDialogOpen} />
      )}
      {isRecordingVisible && (
        <div style={{
          position: 'fixed',
          bottom: '1.5rem',
          right: '1.5rem',
          backgroundColor: recordingRef.current
            ? 'rgba(120, 130, 145, 0.85)'
            : 'rgba(170, 205, 235, 0.85)',
          color: '#1f2937',
          padding: '0.75rem 1.5rem',
          borderRadius: '0.75rem',
          boxShadow: '0 6px 12px rgba(0,0,0,0.12)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.4)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          fontSize: '1rem'
        }}>
          <span
            className={recordingRef.current ? "pulsing-mic" : ""}
            style={{ fontSize: "1.4rem", lineHeight: 1 }}
          >
            🎙️
          </span>
          {recordingRef.current ? 'Aufnahme läuft...' : 'Wird verarbeitet...'}
          {recordingRef.current && (
            <button
              onClick={() => {
                if (recordingRef.current) {
                  mediaRecorder?.stop();
                  recordingRef.current = false;
                  setIsRecordingVisible(true);
                }
              }}
              style={{
                backgroundColor: 'rgba(255,255,255,0.9)',
                color: '#1f2937',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.25rem 0.75rem',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Stopp
            </button>
          )}
        </div>
      )}
    </>
  );
}
export default Documentation;   
